/**
 * Flash Sale Countdown
 * Vanilla JS, no dependencies. The section renders all three campaign states
 * server-side (Liquid decides which one starts visible, using the shop's own
 * clock, so there's no flash-of-wrong-state). This script only ticks the
 * numbers and handles the live before→live→ended transitions without a page
 * reload, using each visitor's own clock against the UTC-anchored timestamps
 * Liquid computed from the shop's timezone.
 */
(function () {
  if (customElements.get('flash-sale-countdown')) return;

  class FlashSaleCountdown extends HTMLElement {
    constructor() {
      super();

      this.startDate = new Date(this.dataset.startIso);
      this.endDate = new Date(this.dataset.endIso);
      this.state = this.dataset.state; // 'before' | 'live' | 'ended', set server-side
      this.autoHide = this.dataset.autoHide === 'true';
      this.confettiEnabled = this.dataset.enableConfetti === 'true';
      this.messageRotateMs = (parseInt(this.dataset.messageRotateSeconds, 10) || 3) * 1000;
      this.stickyMobile = this.classList.contains('flash-sale--sticky-mobile');
      this.reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.stateEls = {
        before: this.querySelector('[data-flash-sale-state="before"]'),
        live: this.querySelector('[data-flash-sale-state="live"]'),
        ended: this.querySelector('[data-flash-sale-state="ended"]'),
      };

      this.confettiContainer = this.querySelector('[data-flash-sale-confetti]');
      this.progressFill = this.querySelector('[data-flash-sale-progress-fill]');
      this.progressBar = this.querySelector('[data-flash-sale-progress]');

      this.tickTimer = null;
      this.messageTimer = null;
      this.lastRendered = {}; // per-unit last-rendered value, to only flip on real change
    }

    connectedCallback() {
      this.tick();
      this.tickTimer = setInterval(this.tick.bind(this), 1000);

      this.setupMessageRotation();
      if (this.stickyMobile) this.setupStickyCondensing();
    }

    disconnectedCallback() {
      clearInterval(this.tickTimer);
      clearInterval(this.messageTimer);
    }

    /* ------------------------------------------------------------------ */
    /* Countdown tick                                                      */
    /* ------------------------------------------------------------------ */

    tick() {
      const now = new Date();
      const target = this.state === 'before' ? this.startDate : this.endDate;
      const diffMs = target.getTime() - now.getTime();

      if (diffMs <= 0) {
        if (this.state === 'before') {
          this.transitionTo('live');
        } else if (this.state === 'live') {
          this.transitionTo('ended');
        }
        // Fall through so the newly-active state renders 00:00:00:00 / hides
        // immediately rather than waiting a full extra second.
      }

      const remaining = Math.max(diffMs, 0);
      const units = FlashSaleCountdown.splitDuration(remaining);

      // Batch the DOM writes for this tick into a single animation frame —
      // four elements update "simultaneously" every second; doing that inside
      // one rAF callback avoids layout thrashing across separate ticks.
      requestAnimationFrame(() => {
        this.renderUnits(units);
        if (this.state === 'live') this.updateProgress();
      });
    }

    static splitDuration(ms) {
      const totalSeconds = Math.floor(ms / 1000);
      return {
        days: Math.floor(totalSeconds / 86400),
        hours: Math.floor((totalSeconds % 86400) / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60,
      };
    }

    renderUnits(units) {
      const activeStateEl = this.stateEls[this.state];
      if (!activeStateEl) return;

      Object.keys(units).forEach((unit) => {
        const value = String(units[unit]).padStart(2, '0');
        const el = activeStateEl.querySelector('[data-flash-sale-unit="' + unit + '"]');
        if (!el) return;

        if (this.lastRendered[unit] !== value) {
          el.textContent = value;
          if (!this.reducedMotion) {
            el.classList.remove('is-flipping');
            // Re-trigger the animation even if the class never fully left
            // (rare, but possible under a slow main thread).
            void el.offsetWidth;
            el.classList.add('is-flipping');
          }
          this.lastRendered[unit] = value;
        }
      });
    }

    /* ------------------------------------------------------------------ */
    /* State transitions                                                   */
    /* ------------------------------------------------------------------ */

    transitionTo(newState) {
      const previousState = this.state;
      this.state = newState;
      this.setAttribute('data-state', newState);
      this.lastRendered = {};

      if (this.stateEls[previousState]) this.stateEls[previousState].hidden = true;
      if (this.stateEls[newState]) this.stateEls[newState].hidden = false;

      if (newState === 'live' && previousState === 'before' && this.confettiEnabled) {
        this.fireConfetti();
      }

      if (newState === 'ended') {
        clearInterval(this.tickTimer);
        if (this.autoHide) {
          this.hidden = true;
          this.style.display = 'none';
        }
      }
    }

    /* ------------------------------------------------------------------ */
    /* Progress bar                                                        */
    /* ------------------------------------------------------------------ */

    updateProgress() {
      if (!this.progressFill) return;
      const total = this.endDate.getTime() - this.startDate.getTime();
      const elapsed = Date.now() - this.startDate.getTime();
      const percent = Math.min(100, Math.max(0, (elapsed / total) * 100));

      this.progressFill.style.width = percent + '%';
      if (this.progressBar) this.progressBar.setAttribute('aria-valuenow', Math.round(percent));
    }

    /* ------------------------------------------------------------------ */
    /* Rotating urgency / announcement messages                            */
    /* ------------------------------------------------------------------ */

    setupMessageRotation() {
      const messages = Array.prototype.slice.call(this.querySelectorAll('.flash-sale__message'));
      if (messages.length < 2) return;

      let index = messages.findIndex(function (m) {
        return m.classList.contains('is-active');
      });
      if (index === -1) index = 0;

      this.messageTimer = setInterval(() => {
        messages[index].classList.remove('is-active');
        index = (index + 1) % messages.length;
        messages[index].classList.add('is-active');
      }, this.messageRotateMs);
    }

    /* ------------------------------------------------------------------ */
    /* Confetti (fired once, on before→live transition)                    */
    /* ------------------------------------------------------------------ */

    fireConfetti() {
      if (!this.confettiContainer || this.reducedMotion) return;

      const colors = ['#ef3178', '#ffffff', '#ffd166', '#7ee787'];
      const pieceCount = 60;
      const fragment = document.createDocumentFragment();

      for (let i = 0; i < pieceCount; i++) {
        const piece = document.createElement('span');
        piece.className = 'flash-sale__confetti-piece';
        const left = Math.random() * 100;
        const duration = 2.2 + Math.random() * 1.6;
        const delay = Math.random() * 0.4;
        const color = colors[i % colors.length];

        piece.style.left = left + 'vw';
        piece.style.background = color;
        piece.style.animationDuration = duration + 's';
        piece.style.animationDelay = delay + 's';
        piece.style.borderRadius = i % 3 === 0 ? '50%' : '0';

        fragment.appendChild(piece);
      }

      this.confettiContainer.appendChild(fragment);

      setTimeout(() => {
        this.confettiContainer.innerHTML = '';
      }, 4500);
    }

    /* ------------------------------------------------------------------ */
    /* Sticky mobile condensing                                            */
    /* ------------------------------------------------------------------ */

    setupStickyCondensing() {
      const sentinel = document.createElement('div');
      sentinel.style.height = '1px';
      this.insertAdjacentElement('afterend', sentinel);

      const mobileQuery = window.matchMedia('(max-width: 749px)');

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const shouldCondense = !entry.isIntersecting && mobileQuery.matches;
          this.setAttribute('data-condensed', shouldCondense ? 'true' : 'false');
        });
      });

      observer.observe(sentinel);
    }
  }

  customElements.define('flash-sale-countdown', FlashSaleCountdown);
})();
